import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { Button, TextField } from './controls';
import { ErrorNotice } from './feedback';

/**
 * Dialog auf Basis von `<dialog>.showModal()`: Fokusfalle und Esc kommen vom Browser.
 * Esc schließt nicht, solange eine Aktion läuft (design.md §7).
 */
export function Dialog({
  open,
  title,
  onClose,
  busy,
  children,
  footer,
}: {
  open: boolean;
  title: string;
  onClose: () => void;
  busy?: boolean;
  children: ReactNode;
  footer: ReactNode;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  // Eindeutig pro Dialog: mehrere Dialoge auf einer Seite dürfen sich den Namen nicht teilen.
  const titleId = useId();
  useEffect(() => {
    const d = ref.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  return (
    <dialog
      ref={ref}
      aria-labelledby={titleId}
      onCancel={(e) => {
        e.preventDefault();
        if (!busy) onClose();
      }}
    >
      <div className="dlg-body">
        <h2 id={titleId}>{title}</h2>
        {children}
      </div>
      <div className="dlg-foot">{footer}</div>
    </dialog>
  );
}

/**
 * Bestätigung destruktiver Aktionen: Folgen konkret benennen, Pflichtfeld Grund (geht ins Audit-Log),
 * Eingabe des Namens/Codes zur Bestätigung; erst dann ist der Button aktiv.
 */
export function ConfirmDialog({
  open,
  title,
  consequence,
  confirmText,
  confirmLabel = 'Endgültig ausführen',
  needReason = true,
  onConfirm,
  onClose,
}: {
  open: boolean;
  title: string;
  consequence: ReactNode;
  /** Muss exakt eingetippt werden, z. B. der Name. `undefined` = keine Namenseingabe. */
  confirmText?: string;
  confirmLabel?: string;
  needReason?: boolean;
  onConfirm: (reason: string) => Promise<unknown>;
  onClose: () => void;
}) {
  const [reason, setReason] = useState('');
  const [typed, setTyped] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);

  useEffect(() => {
    if (open) {
      setReason('');
      setTyped('');
      setError(null);
    }
  }, [open]);

  const reasonOk = !needReason || reason.trim().length >= 3;
  const typedOk = confirmText === undefined || typed.trim() === confirmText;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await onConfirm(reason.trim());
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open={open}
      title={title}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant="danger-solid" disabled={!reasonOk || !typedOk} busy={busy} busyText="Wird ausgeführt…" onClick={submit}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <p>{consequence}</p>
      {needReason && (
        <TextField
          label="Grund"
          help="Pflicht, mindestens 3 Zeichen. Wird im Audit-Log gespeichert."
          value={reason}
          maxLength={500}
          onChange={(e) => setReason(e.target.value)}
          wide
          autoFocus
        />
      )}
      {confirmText !== undefined && (
        <TextField
          label={`Zur Bestätigung „${confirmText}“ eingeben`}
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
          autoComplete="off"
          wide
        />
      )}
      {error !== null && <ErrorNotice error={error} />}
    </Dialog>
  );
}

/**
 * Umbenennen (Kiste, Team): Name vorbelegt, bei Admin-Aktionen Pflichtfeld Grund fürs Audit-Log (`needReason`).
 * `allowEmpty`: leerer Name entfernt ihn (`null`), sonst ist Speichern erst mit Namen möglich.
 */
export function RenameDialog({
  title,
  label = 'Name',
  current,
  maxLength,
  allowEmpty = false,
  needReason = true,
  help,
  onSave,
  onClose,
}: {
  title: string;
  label?: string;
  current: string | null;
  maxLength: number;
  allowEmpty?: boolean;
  needReason?: boolean;
  help?: string;
  onSave: (name: string | null, reason: string) => Promise<void>;
  onClose: () => void;
}) {
  const [name, setName] = useState(current ?? '');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  useEffect(() => setError(null), [name, reason]);

  const nameOk = allowEmpty || name.trim().length > 0;
  const reasonOk = !needReason || reason.trim().length >= 3;
  const save = async () => {
    setBusy(true);
    try {
      await onSave(name.trim() || null, reason.trim());
      onClose();
    } catch (err) {
      setError(err);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog
      open
      title={title}
      onClose={onClose}
      busy={busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            Abbrechen
          </Button>
          <Button variant="primary" onClick={save} busy={busy} disabled={!nameOk || !reasonOk}>
            Speichern
          </Button>
        </>
      }
    >
      <TextField
        label={label}
        value={name}
        maxLength={maxLength}
        onChange={(e) => setName(e.target.value)}
        help={help ?? (allowEmpty ? 'Leer lassen entfernt den Namen.' : `1–${maxLength} Zeichen.`)}
        wide
        autoFocus
      />
      {needReason && (
        <TextField label="Grund" value={reason} maxLength={500} onChange={(e) => setReason(e.target.value)} help="Pflicht, wird im Audit-Log gespeichert." wide />
      )}
      {error !== null && <ErrorNotice error={error} />}
    </Dialog>
  );
}
